import type {
    ClassicalGrid,
    ClassicalListShelf,
    ClassicalSection,
    ClassicalShelf,
} from '@jet-app/music-classical/api/types';
import type { Section } from '@jet-app/music-classical/models/section/Section';
import { transformToGridSection } from './grid';
import { transformToShelfList, transformToShelf } from './shelf';
import { transformToInfoShelf } from './info';
import { transformToTrackListSection } from './track-list';
import { transformToTrackListFooter } from './track-list-footer';
import { inject, type ObjectGraph } from '@jet/environment/dependencies';
import { makeSearchPageIntent } from '@jet-app/music-classical/intents/search/SearchPageIntent';
import {
    createFlowActionForAction,
    getIntentBuilderFnForType,
} from '../create-flow-action';
import { ObjectGraphType } from '../../dependencies';

export function transformSections(
    sections: ClassicalSection[],
    objectGraph: ObjectGraph,
    screenType?: string, // TODO: Create enum for this?
    screenId?: string,
    prominentItemIdentifier?: string,
): Section<unknown>[] {
    const logger = inject(ObjectGraphType.logger, objectGraph);
    let foundBioSectionIndex = null;

    return sections?.reduce((transformedSections, section, sectionIndex) => {
        if (section.type === 'see-all') {
            logger.log(
                'Skipping see all list section as it has been added along with previous section',
                section.type,
            );
            return transformedSections;
        } else if (
            section.type === 'track-list' &&
            screenType !== 'searchResult'
        ) {
            // Creates an array of sections split up to group works together
            const trackListSections = transformToTrackListSection(
                section,
                objectGraph,
                screenType,
                screenId,
                prominentItemIdentifier,
            );

            // Push each of the sections into the transformedSections accumulator
            // so it shows up as it's own section
            trackListSections?.forEach((sec) => {
                transformedSections.push(sec);
            });
        } else if (section.type === 'track-list-footer') {
            const component = section?.components?.[0];

            transformedSections.push(
                transformToTrackListFooter(
                    component as unknown as ClassicalListShelf,
                    objectGraph,
                    screenType,
                ),
            );
        } else if (section.type === 'credits') {
            const component = section?.components?.[0];

            transformedSections.push(
                transformToInfoShelf(
                    component as unknown,
                    section.heading?.title,
                    'credit',
                ),
            );
        } else if (section.type === 'bio') {
            const component = section?.components?.[0];

            transformedSections.push(
                transformToInfoShelf(
                    component as unknown,
                    section.heading?.title,
                    'paragraph',
                ),
            );

            foundBioSectionIndex = transformedSections.length - 1;
        } else if (section?.type === 'search-examples') {
            const component = section?.components?.[0];
            transformedSections.push(
                transformToGridSection(
                    component as unknown as ClassicalGrid,
                    'linkLockup',
                    makeSearchPageIntent,
                    objectGraph,
                    false,
                    section?.heading?.title,
                ),
            );
        } else if (['periods', 'genres'].includes(section?.type)) {
            const component = section?.components?.[0];

            transformedSections.push(
                transformToGridSection(
                    component as unknown as ClassicalGrid,
                    'rectangularLinkLockup',
                    null,
                    objectGraph,
                ),
            );
        } else {
            section?.components.forEach((component) => {
                switch (component.type) {
                    case 'grid': {
                        // Section Type: Browse & Component Type: Grid to be rendered as Brick Lockups
                        if (section?.type === 'browse') {
                            // Browse > Catalog grid may have more content types than just 'catalog'.  Most of
                            // these are processed as catalog actions, but there may be others that need to be
                            // special cased.  These special cases are handled in the transformToGridSection() transform.
                            // Ensuring that some 'catalog' items exist so we don't affect other detail tabs like Instruments
                            const hasCatalog = component?.items?.some(
                                (item) => item.action?.screenType === 'catalog',
                            );
                            const actionIntentBuilderFn =
                                getIntentBuilderFnForType(
                                    hasCatalog
                                        ? 'catalog'
                                        : component?.items?.[0]?.action
                                              ?.screenType,
                                );
                            transformedSections.push(
                                transformToGridSection(
                                    component as unknown as ClassicalGrid,
                                    'brickLockup',
                                    actionIntentBuilderFn,
                                    objectGraph,
                                ),
                            );
                        }
                        break;
                    }
                    case 'list': {
                        if (shouldRenderShelfList(section, screenType)) {
                            // The `shelfListItem` specifically on Recording view
                            // and immediately after the `track-list-footer`
                            // receives a slightly different treatment
                            // More info: rdar://139297135 (Classical on Web:  Add Recordings Footer Component)
                            const isFromAlbum =
                                screenType === 'recording' &&
                                sections[sectionIndex - 1]?.type ===
                                    'track-list-footer';

                            transformedSections.push(
                                transformToShelfList(
                                    component as unknown as ClassicalListShelf,
                                    section.heading?.title,
                                    objectGraph,
                                    screenType,
                                    isFromAlbum,
                                ),
                            );
                        } else {
                            transformedSections.push(
                                transformToShelf(
                                    component as unknown as ClassicalShelf,
                                    section.heading,
                                    component.action?.screenType,
                                    objectGraph,
                                ),
                            );
                        }
                        break;
                    }
                    case 'shelf': {
                        transformedSections.push(
                            transformToShelf(
                                component as unknown as ClassicalShelf,
                                section.heading,
                                component.action?.screenType,
                                objectGraph,
                            ),
                        );
                        break;
                    }
                    case 'moreText': {
                        transformedSections.push(
                            transformToInfoShelf(
                                component as unknown,
                                section.heading?.title,
                                'paragraph',
                            ),
                        );
                        break;
                    }
                    default: {
                        console.warn(
                            'Unrecognized classical component found, for which no transformation exists: ',
                            component,
                        );
                        return null;
                    }
                }

                // Temporary Code to Pull Up the See All Section (if present as next section) and put it on the section it was succeeding
                // TODO: Remove this code once API Team resolves this radar -
                // rdar://143314264 ([Classical on Web] Support Shelf type Layout for Search Results Page)
                // rdar://142978668 ([Web] See All Links throughout the Web Shelves should not be present in the items but present as an action on the header similar to Home (Listen Now) shelves)
                if (sections[sectionIndex + 1]?.type === 'see-all') {
                    const seeAllSection = sections[
                        sectionIndex + 1
                    ] as unknown as ClassicalSection;
                    const seeAllItem =
                        seeAllSection?.components?.[0]?.items?.[0];
                    const segue = createFlowActionForAction(
                        seeAllItem?.action,
                        objectGraph,
                        {
                            section: seeAllItem.action?.location,
                            query: seeAllItem?.action?.url,
                        },
                    );
                    transformedSections[transformedSections?.length - 1].segue =
                        segue;
                }
            });
        }

        if (foundBioSectionIndex) {
            addBackgroundTreatmentAfterFirstBio(
                transformedSections,
                foundBioSectionIndex,
            );
        }

        return transformedSections.filter((section) => !!section);
    }, []);
}

function shouldRenderShelfList(
    section: ClassicalSection,
    screenType: string,
): boolean {
    const componentType = section.components[0].type;
    const itemType = section.components[0]?.items[0]?.type;

    // Only for the Search Results view, we should render square lockups for
    // albums even if the section specifies a list view
    // rdar://145171929 ([Classical on Web] Albums shelf on search results page is using wrong lockup type)
    if (screenType === 'searchResult' && itemType === 'album') {
        return false;
    }

    return (
        (componentType === 'list' &&
            ['album', 'album-with-actions', 'recording', 'work'].includes(
                itemType,
            )) ||
        section?.type === 'track-list' // rdar://141786178 ([Classical on Web]: Need special Updates to ListShelf to render Tracks Subheading separately according to Designs in Quip)
    );
}

function addBackgroundTreatmentAfterFirstBio(
    sections: Array<Section<unknown>>,
    bioStart: number,
) {
    let filteredSections = sections.filter(Boolean);

    for (let i = bioStart; i < filteredSections.length; i++) {
        filteredSections[i].backgroundTreatment = 'alternate';
    }
}
